import React, { memo, useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import _cloneDeep from 'lodash/cloneDeep';

import { useFeatures } from '@atc/features';

import { LinkIcon, Section } from 'reaxl';
import { sendClick } from 'reaxl-analytics';

import offsetCalc from '@/utilities/offSetScrollCalculation';
import scrollToElement from '@/utilities/scrollToElement';

import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import inspectionReportTokenFetcher from '@/fetchers/InspectionReportTokenFetcher';
import VehicleConditioningFetcher from '@/fetchers/vdp/VehicleConditioningFetcher';

import BatteryDegradationJumpLink from '@/components/vehicledetails/BatteryDegradationJumpLink';
import BatteryInfoModal from '@/components/vehicledetails/BatteryInfoModal';
import SpecificationList from '@/components/vehicledetails/SpecificationList';
import WarrantyJumpLink from '@/components/vehicledetails/WarrantyJumpLink/WarrantyJumpLink';

// the purpose of specKeys is to sort the order of specifications
// NOTE: vehicle reconditioning will always be at the bottom
const specKeys = [
    'wellEquipped',
    'mileage',
    'engineDescription',
    'mpg', // only apply to non-electric cars
    'batteryDegradationInfo',
    'evBatteryChargeTime', // only apply to hybrid and electric cars
    'exterior',
    'interiorSeats',
    'transmission',
    'driveType',
    'truckBedLength', // only apply to trucks
    'windowSticker',
    'warranty',
    'inspectionReport', // only for FBA vehicles
];

let gasMileageDisclaimer = 'Miles per gallon (MPG) shown is estimated for city/highway driving and may be combined. Actual mileage will vary.';
const evRangeDisclaimer = 'EV mile range is the distance an electric vehicle can drive on electricity before the battery depletes.';

const icons = {
    mileage: 'mileage',
    exterior: 'exterior',
    interiorSeats: 'interiorSeats',
    engineDescription: 'engineDescription',
    transmission: 'transmission',
    driveType: 'driveType',
    mpg: 'mpg',
    truckBedLength: 'truckBedLength',
    evBatteryChargeTime: 'battery',
    windowSticker: 'windowSticker',
    inspectionReport: 'inspectionChecklist',
    warranty: 'warranty',
    wellEquipped: 'diamond',
    batteryDegradationInfo: 'hybridSuvs',
};

const addSectionIcon = (specifications, key) => {
    if (specifications?.[key]) {
        specifications[key].icon = icons[key];
    }
};

const handleFuelTypeGroup = (fuelTypeGroup, engineDescription) => {
    switch (fuelTypeGroup) {
        case 'Hybrid: Gas/Electric':
            engineDescription.value = fuelTypeGroup;
            break;
        case 'Plug-in Hybrid: Gas/Electric':
            engineDescription.value = fuelTypeGroup;
            break;
        default:
            break;
    }
};

function SpecificationsGridContainer({
    bordered = true,
}) {
    const {
        battery_health: [enableBatteryHealth, {
            ford_battery_health: fordBatteryHealth,
        }],
        specifications: [, {
            low_mileage: enableLowMileage,
            well_equipped_vehicle: enableWellEquippedVehicle,
        }],
        inspection_checklist: [isInspectionChecklistEnabled],
        vdp_safeguard: [isSafeguardEnabled],
        custom_mpg_disclaimers: [, { disclaimers }],
        vehicle_reconditioning: [enableVehicleReconditioning],
    } = useFeatures([
        'battery_health',
        'specifications',
        'inspection_checklist',
        'vdp_safeguard',
        'custom_mpg_disclaimers',
        'vehicle_reconditioning',
    ]);

    const [vehicleReconditioningSpecifications, setVehicleReconditioningSpecifications] = useState();

    const {
        electricVehicleRange = '',
        esntial = {},
        owner: { nameplate = [] } = {},
        mpgCity,
        mpgHighway,
        specifications: initSpecifications = {},
        fyuseId,
        vin,
        listingType,
        exteriorColorSimple,
        interiorColorSimple,
        windowSticker: { href: windowStickerHref } = {},
        returnPolicy = {},
        moneyBackGuarantee,
        safeGuardProgram,
        fordSalesCode,
        makeCode,
        fuelType,
        fuelTypeGroup = '',
        vinSightData = {},
        electricComponentInfo = {},
    } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const batteryHealthExclusions = electricComponentInfo?.batteryDegradationInfo?.ratingSource !== 'OEM';
    const displayBatteryHealth = (enableBatteryHealth && !fordBatteryHealth) && electricComponentInfo?.batteryDegradationInfo && batteryHealthExclusions;

    const specifications = useMemo(() => _cloneDeep(initSpecifications), [initSpecifications]);

    let warrantyProducts = {
        returnPolicy: Object.values(returnPolicy).filter(Boolean).length ? returnPolicy : undefined,
        moneyBackGuarantee: moneyBackGuarantee || undefined,
        safeGuardProgram,
    };

    warrantyProducts = Object.values(warrantyProducts).filter(Boolean).length ? warrantyProducts : undefined;

    const updateInspectionChecklistUrl = (async () => {
        const response = await inspectionReportTokenFetcher();
        const token = response?.authorization_token;
        const updatedSalesCode = fordSalesCode ? fordSalesCode.replace(/\D/g, '') : '';
        return `https://dfc.concentrix.com/dfc/inspection-checklist?vin=${vin}&authorization_token=${token}&sales_code=${updatedSalesCode}`;
    });

    useEffect(() => {
        if (enableVehicleReconditioning && vin && fyuseId) {
            (async () => {
                const vehicleReconditioningResponse = await VehicleConditioningFetcher(vin);
                setVehicleReconditioningSpecifications(vehicleReconditioningResponse);
            })();
        }
    }, [enableVehicleReconditioning, fyuseId, vin]);

    // Jump link only enabled if safeguard FF is on (even if other data exist) or listing is Esntial ineligible
    if (!isSafeguardEnabled || Object.values(esntial).filter(Boolean).length) {
        warrantyProducts = null;
    }

    if (enableBatteryHealth && fordBatteryHealth) {
        if (electricVehicleRange > 0) {
            specifications.batteryDegradationInfo = {
                label: 'EV RANGE',
                value: `${electricVehicleRange.toLocaleString('en-US')} mi Range When New`,
            };
        }

        if (electricComponentInfo?.epaChargeTimeAt240V) {
            specifications.evBatteryChargeTime = {
                value: `Estimated ${electricComponentInfo.epaChargeTimeAt240V} hr Charging Time`,
            };
        }
    }

    if (warrantyProducts) {
        const handleWarrantyLinkClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            scrollToElement('purchaseConfidence', offsetCalc(25, 'sticky-header-vdp'));
        };
        specifications.warranty = {
            label: 'Purchase With Confidence',
            value: <WarrantyJumpLink
                {...warrantyProducts}
                onClick={handleWarrantyLinkClick}
            />,
        };
        specifications.warranty.icon = 'warranty';
    }

    // Some dealerships require specific wording for the MPG disclaimer
    if (nameplate.includes(makeCode) && Object.keys(disclaimers).includes(makeCode)) {
        gasMileageDisclaimer = disclaimers[makeCode];
    }
    if (!specifications.range && fuelTypeGroup !== 'Electric' && fuelType?.name !== 'Electric') {
        specifications.mpg = {
            label: 'MPG',
            value: mpgCity && mpgHighway ? mpgCity + ' City / ' + mpgHighway + ' Highway' : '',
            disclaimer: gasMileageDisclaimer,
        };
    }

    if (windowStickerHref) {
        const windowStickerLabel = 'View Original Window Sticker';
        const handleWindowStickerClick = (event) => {
            sendClick('windowStickerClick', event, { co_txt_url: windowStickerLabel });
        };
        specifications.windowSticker = {
            label: 'WINDOW STICKER',
            value: (
                <LinkIcon
                    href={windowStickerHref}
                    glyph="offsite"
                    target="_blank"
                    rel="nofollow noopener"
                    onClick={handleWindowStickerClick}
                >
                    {windowStickerLabel}
                </LinkIcon>
            ),
        };
    }

    // inspection checklist
    if (isInspectionChecklistEnabled) {
        const inspectionReportLabel = 'View Inspection Checklist';

        const handleInspectionReportClick = (event) => {
            sendClick('inspectionChecklistClick', event, { co_txt_url: inspectionReportLabel });

            // This window.open hack is needed because iOS wont allow us to call window.open inside of an async
            // function. So we call the new tab outside of the async function then set the url location once we
            // get the authorization token back from the fetcher.
            const newTab = window.open('about:blank');
            updateInspectionChecklistUrl().then((url) => {
                newTab.location = url;
            });
        };
        specifications.inspectionReport = {
            label: 'INSPECTION REPORT',
            value: (
                <LinkIcon
                    glyph="offsite"
                    onClick={handleInspectionReportClick}
                >
                    {inspectionReportLabel}
                </LinkIcon>
            ),
        };
    }

    if (vinSightData?.equipmentIndex >= 5 && enableWellEquippedVehicle) {
        specifications.wellEquipped = {
            label: 'WELL-EQUIPPED VEHICLE',
            value: 'Well-Equipped Vehicle',
            disclaimer: 'This vehicle is better equipped than 50% of  similar vehicles',
        };
    }

    if (listingType !== 'New' && vinSightData?.vehicleMileageIndex <= 5 && enableLowMileage) {
        specifications.mileage = {
            label: 'LOW MILEAGE',
            value: specifications?.mileage?.value,
            icon: 'mileage',
            disclaimer: 'This vehicle has a lower mileage than 50% of similar vehicles',
        };
    }

    if (Object.keys(specifications).length) {
        const {
            engineDescription,
            exterior = {},
            evBatteryChargeTime = {},
            interiorSeats = {},
            transmission = {},
            truckBedLength = {},
            truckBedSizeDescription = {},
            batteryDegradationInfo = {},
        } = specifications;

        // icon attribute will be used a key to render SpecificationIcon in SpecificationList.js
        Object.keys(specifications).forEach((key) => addSectionIcon(specifications, key));

        handleFuelTypeGroup(fuelTypeGroup, engineDescription);

        if (evBatteryChargeTime?.value) {
            evBatteryChargeTime.ctaEl = <BatteryInfoModal />;
        }

        if (displayBatteryHealth && batteryDegradationInfo?.label === 'USABLE RANGE') {
            batteryDegradationInfo.ctaEl = <BatteryDegradationJumpLink ctaCopy="View Details" />;
        } else {
            batteryDegradationInfo.disclaimer = evRangeDisclaimer;
        }

        // exterior
        if (exterior.value && !exterior.value.includes('Exterior')) {
            exterior.value += ' Exterior';
        }
        // icon and colorValue attributes will render ColorSwatch in SpecificationList.js
        exterior.colorValue = exteriorColorSimple;
        // interiorSeat
        if (interiorSeats.value && !interiorSeats.value.toLowerCase().includes('seat') && !interiorSeats.value.includes('Interior')) {
            interiorSeats.value += ' Interior';
        }
        interiorSeats.colorValue = interiorColorSimple;

        // transmission
        if (transmission.value && !transmission.value.includes('Transmission')) {
            transmission.value += ' Transmission';
        }

        // truckBedLength
        if (Object.keys(truckBedLength).length && Object.keys(truckBedSizeDescription).length) {
            if (truckBedLength.value && !truckBedLength.value.includes('Bed') && truckBedSizeDescription.value) {
                truckBedLength.value += ` Bed Length (${truckBedSizeDescription.value})`;
            }
        }
    }

    return (
        <Section
            alignment="left"
            uiContext="section-2"
            data-cmp="specificationsGridContainer"
        >
            <SpecificationList
                vin={vin}
                specKeys={specKeys}
                specifications={specifications}
                vehicleReconditioningSpecs={vehicleReconditioningSpecifications}
                bordered={bordered}
            />
        </Section>
    );
}

export default memo(SpecificationsGridContainer);
